import { useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import axiosInstance, { getUserDetails, hasScientistPondUIAccess } from '../../components/content/axiosConfig';
import Preloader from '../../components/custom/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/toast/toast';
import {
  ForageDropDownSelection,
  TypeDropDownSelection,
  WaterSupplyDropDownSelection,
} from '../../components/custom/DropDownSelections';
import FishSpeciesDropDownSelection from '../../components/custom/FishSpeciesDropDownSelection';
import AgeOfPondSelector from '../../components/custom/AgeOfPondSelector';
import { toDateInputValue } from '../../utils/toDateInputValue';

const UpdatePond = () => {
  const history = useHistory();
  const { id: pondID } = useParams();
  const userDetails = getUserDetails();
  const sessionFisherId = userDetails?.FisherID;
  const [pondOwnerFisherId, setPondOwnerFisherId] = useState(null);
  const fisherID = pondOwnerFisherId ?? sessionFisherId;
  const canEditPondStructure = hasScientistPondUIAccess(userDetails);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFishSpecies, setSelectedFishSpecies] = useState([]);
  const [selectedForage, setSelectedForage] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedWaterSupply, setSelectedWaterSupply] = useState([]); // Changed to array for multiple selection
  const [formData, setFormData] = useState({
    Name: '',
    Location: '',
    FishSpecies: [],
    Forage: null,
    ForageType: '',
    Type: null,
    Age: '',
    AgeInYears: null,
    AgeType: null,
    Size: '',
    Depth: '',
    MaximumDepth: '',
    AverageDepth: '',
    EstimatedVolume: null,
    EstimatedInitialStockingDate: null,
    InitialStockingDateType: null,
    BeginManagementDate: '',
    SupplementalStockingDates: [],
    AquaticVegetationPresent: '',
    WaterSupply: null,  
    FisherID: fisherID
  });

 
  useEffect(() => {
    const fetchPondData = async () => {
      try {
        // Fetch pond details
        const pondResponse = await axiosInstance.get(`/api/Pond/GetPondByID`, {
          params: { id: pondID },
        });
        const pond = pondResponse.data;
        setPondOwnerFisherId(pond.FisherID ?? null);
   
        // Fetch associated fish species
        const fishResponse = await axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondID}`);
        const fishSpecies = fishResponse.data.map((fish) => fish.FishTypeID);

        console.log("Here are fish Species", fishSpecies);

        // Handle water supply data - split comma-separated string into array
        const waterSupplyArray = pond.WaterSupply
          ? pond.WaterSupply.split(',').map(item => item.trim()).filter(item => item) // Remove empty strings
          : [];

        // Handle stocking dates
        const stockingDates = pond.stockingDates || [];

        // Initialize form data
        setFormData({
          Name: pond.Name || '',
          Location: pond.Location || '',
          FishSpecies: fishSpecies,
          Forage: pond.Forage || null, // Keep as string for form submission
          ForageType: pond.ForageType,
          Type: pond.Type || null,
          Age: pond.Age, // Format date to YYYY-MM-DD
          AgeInYears: pond.AgeInYears,
          AgeType: pond.AgeType,
          Size: pond.Size ? pond.Size.toString() : '',
          Depth: pond.Depth ? pond.Depth.toString() : '', // Keep for backward compatibility
          MaximumDepth: pond.MaximumDepth ? pond.MaximumDepth.toString() : (pond.Depth ? pond.Depth.toString() : ''),
          AverageDepth: pond.AverageDepth ? pond.AverageDepth.toString() : '',
          EstimatedVolume: pond.EstimatedVolume,
          EstimatedInitialStockingDate: toDateInputValue(pond.EstimatedInitialStockingDate) || null,
          InitialStockingDateType: pond.InitialStockingDateType,
          BeginManagementDate: toDateInputValue(pond.BeginManagementDate),
          SupplementalStockingDates: stockingDates.map(sd => ({
            id: sd.id,
            StockingDateType: sd.StockingDateType,
            StockingDate: sd.StockingDate ? toDateInputValue(sd.StockingDate) || null : null
          })),
          AquaticVegetationPresent: pond.AquaticVegetationPresent || '',
          WaterSupply: pond.WaterSupply || null,
          FisherID: pond.FisherID ?? sessionFisherId,
        });

        const forageValues = pond.Forage
          ? String(pond.Forage)
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];

        // Set dropdown values
        setSelectedFishSpecies(fishSpecies);
        setSelectedForage(forageValues);
        setSelectedType(pond.Type || null);
        setSelectedWaterSupply(waterSupplyArray); // Set as array for multiple selection

        setIsLoading(false);
      } catch (error) {
        console.log(error);
        setIsLoading(false);
        handleApiError(error, 'Failed to load pond data');
      }
    };
    fetchPondData();
  }, [pondID, sessionFisherId]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [id]: type === 'checkbox' ? checked : value,
      };
      
      // Calculate EstimatedVolume when AverageDepth or Size changes
      if (id === 'AverageDepth' || id === 'Size') {
        const avgDepth = id === 'AverageDepth' ? parseFloat(value) : parseFloat(prev.AverageDepth);
        const size = id === 'Size' ? parseFloat(value) : parseFloat(prev.Size);
        if (avgDepth && size) {
          updated.EstimatedVolume = avgDepth * size;
        } else {
          updated.EstimatedVolume = null;
        }
      }
      
      return updated;
    });
  };

  const handleApiError = (error, defaultMessage) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.reload();
      showErrorToast('Session expired. Please log in again.');
    } else {
      const errorMessage = error.response?.data?.message || defaultMessage;
      showErrorToast(errorMessage);
      console.error(defaultMessage, error);
    }
  };

  const handleFishSpeciesChange = (selectedValues) => {
    setSelectedFishSpecies(selectedValues);
    setFormData((prev) => ({
      ...prev,
      FishSpecies: selectedValues,
    }));
  };

  const handleForageChange = (selectedOptions) => {
    const forageValues = selectedOptions ? selectedOptions.map((option) => option.value) : [];
    const forageString = forageValues.length ? forageValues.join(',') : null;
    setSelectedForage(forageValues);
    setFormData((prev) => ({
      ...prev,
      Forage: forageString,
    }));
  };

  const handleTypeChange = (selectedOption) => {
    const typeValue = selectedOption ? selectedOption.value : null;
    setSelectedType(selectedOption);
    setFormData((prev) => ({
      ...prev,
      Type: typeValue,
    }));
  };

  // Handler for multiple water supply selection
  const handleWaterSupplyChange = (selectedOptions) => {
    // selectedOptions will be an array when allowMultiple is true
    const waterSupplyValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    const waterSupplyString = waterSupplyValues.join(','); // Convert to comma-separated string

    setSelectedWaterSupply(waterSupplyValues);
    setFormData((prev) => ({
      ...prev,
      WaterSupply: waterSupplyString, // Store as comma-separated string in formData
    }));
  };

  // Handler for Age of Pond
  const handleAgeOfPondChange = (ageData) => {
    setFormData((prev) => ({
      ...prev,
      AgeType: ageData.ageType,
      AgeInYears: ageData.ageInYears,
    }));
  };

  const validateForm = () => {
    if (!formData.Name.trim()) {
      showErrorToast('Pond name is required');
      return false;
    }
    if (!formData.Location.trim()) {
      showErrorToast('Location is required');
      return false;
    }
    if (!formData.Type) {
      showErrorToast('Pond type is required');
      return false;
    }
    if (!formData.Size.trim()) {
      showErrorToast('Pond size is required');
      return false;
    }
    if (!formData.MaximumDepth.trim()) {
      showErrorToast('Maximum depth is required');
      return false;
    }
    return true;
  };

  const savePond = async (exitAfterSave) => {
    if (!canEditPondStructure) {
      return;
    }
    setIsLoading(true);

    if (!validateForm()) {
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        Name: formData.Name,
        Location: formData.Location,
        Forage: formData.Forage,
        Type: formData.Type,
        Age: formData.Age || "",
        AgeInYears: formData.AgeInYears,
        AgeType: formData.AgeType,
        Size: parseFloat(formData.Size) || 0,
        Depth: parseFloat(formData.MaximumDepth) || 0, // Keep Depth for backward compatibility
        MaximumDepth: parseFloat(formData.MaximumDepth) || 0,
        AverageDepth: formData.AverageDepth ? parseFloat(formData.AverageDepth) : null,
        EstimatedVolume: formData.EstimatedVolume,
        EstimatedInitialStockingDate: formData.EstimatedInitialStockingDate
          ? toDateInputValue(formData.EstimatedInitialStockingDate) || null
          : null,
        InitialStockingDateType: formData.InitialStockingDateType,
        BeginManagementDate: toDateInputValue(formData.BeginManagementDate) || null,
        SupplementalStockingDates: (formData.SupplementalStockingDates || []).map((sd) => ({
          ...sd,
          StockingDate: sd.StockingDate ? toDateInputValue(sd.StockingDate) || null : null,
        })),
        ForageType: formData.ForageType,
        AquaticVegetationPresent: formData.AquaticVegetationPresent,
        WaterSupply: formData.WaterSupply,
        FisherID: fisherID,
        FishSpecies: formData.FishSpecies,
      };

      await axiosInstance.put(`/api/Pond/UpdatePond/${pondID}`, payload);

      if (formData.FishSpecies && formData.FishSpecies.length > 0) {
        const existingFishResponse = await axiosInstance.get(`/api/PondFishes/GetAllPondFishes/${pondID}`);
        const existingFishSpecies = existingFishResponse.data.map((fish) => fish.FishTypeID);

        const newFishSpecies = formData.FishSpecies.filter(
          (fishTypeID) => !existingFishSpecies.includes(fishTypeID)
        );
        const updatedFishSpecies = formData.FishSpecies.filter((fishTypeID) =>
          existingFishSpecies.includes(fishTypeID)
        );

        if (newFishSpecies.length > 0) {
          const addPayload = {
            pondFishes: newFishSpecies.map((fishTypeID) => ({
              PondID: parseInt(pondID),
              FishTypeID: parseInt(fishTypeID),
              FisherID: fisherID,
            })),
          };
          await axiosInstance.post('/api/PondFishes/BulkAddPondFishes', addPayload);
        }

        if (updatedFishSpecies.length > 0) {
          const updatePayload = {
            pondFishes: updatedFishSpecies.map((fishTypeID) => ({
              PondID: parseInt(pondID),
              FishTypeID: parseInt(fishTypeID),
              NewFishTypeID: parseInt(fishTypeID),
              FisherID: fisherID,
            })),
          };
          await axiosInstance.put('/api/PondFishes/BulkUpdatePondFishes', updatePayload);
        }

        const removedFishSpecies = existingFishSpecies.filter(
          (fishTypeID) => !formData.FishSpecies.includes(fishTypeID)
        );
        for (const fishTypeID of removedFishSpecies) {
          await axiosInstance.delete(`/api/PondFishes/DeletePondFish/${pondID}/${fishTypeID}`);
        }
      }

      showSuccessToast(
        exitAfterSave ? 'Pond updated — returning to list' : 'Pond saved'
      );
      setIsLoading(false);
      if (exitAfterSave) {
        history.push('/pond');
      }
    } catch (error) {
      setIsLoading(false);
      handleApiError(error, 'Failed to update pond or fish species');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const sub = e.nativeEvent.submitter;
    const action = sub?.getAttribute?.('value') ?? sub?.value ?? 'stay';
    const exitAfterSave = action === 'exit';
    await savePond(exitAfterSave);
  };

  return isLoading ? (
    <Preloader />
  ) : (
    <div className="donezo-page donezo-workflow-page py-3">
      <form onSubmit={handleFormSubmit}>
        <div className="container-fluid px-3 px-md-4">
          {/* Header Section */}
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-4">
            <div className="mb-3 mb-md-0">
              <ul className="breadcrumb mb-2 small">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/pond">Pond</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">Update Pond</li>
              </ul>
              <h1 className="mb-0 page-header donezo-page-header-title d-flex align-items-center gap-3">
                <span
                  className="d-inline-flex align-items-center justify-content-center rounded-3 text-white shadow-sm flex-shrink-0"
                  style={{
                    width: 52,
                    height: 52,
                    background: 'linear-gradient(135deg, #475569 0%, #334155 100%)',
                  }}
                  aria-hidden
                >
                  <i className="fas fa-cog" style={{ fontSize: '1.35rem' }} />
                </span>
                Update Pond
              </h1>
            </div>
            {canEditPondStructure && (
              <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                <button
                  type="submit"
                  name="saveAction"
                  value="stay"
                  className="btn btn-outline-success px-3 px-md-4 py-2 rounded-pill shadow-sm"
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="fa fa-save me-2"></i>
                  Save
                </button>
                <button
                  type="submit"
                  name="saveAction"
                  value="exit"
                  className="btn btn-success px-3 px-md-4 py-2 rounded-pill shadow-sm"
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    backgroundColor: '#28a745',
                    border: 'none',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="fa fa-check me-2"></i>
                  Save and exit
                </button>
              </div>
            )}
          </div>

          {/* Navigation Tabs */}
          <div className="card border-0 shadow-sm mb-4 donezo-workflow-tabs">
            <ul className="nav nav-tabs border-0">
              <li className="nav-item me-2">
                <Link
                  to={`/updatePond/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top donezo-tab-active"
                >
                  <i className="fas fa-cog me-2" aria-hidden />General
                </Link>
              </li>
              <li className="nav-item me-2">
                <Link
                  to={`/pondFeedFertilization/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top"
                >
                  <i className="fas fa-leaf me-2" aria-hidden />
                  Feed &amp; Fertilization
                </Link>
              </li>
              <li className="nav-item me-2">
                <Link
                  to={`/pondStocking/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top"
                >
                  <i className="fas fa-water me-2" aria-hidden />Stocking Data
                </Link>
              </li>
              <li className="nav-item me-2">
                <Link
                  to={`/pondDetailViaPond/${pondID}`}
                  className="nav-link px-3 py-2 rounded-top"
                >
                  <i className="fas fa-chart-line me-2" aria-hidden />
                  Fishing / Sampling Data
                </Link>
              </li>
            </ul>
          </div>

          <div className="row g-3">
            {/* Basic Information Panel */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2 donezo-workflow-card__header">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title">
                    <i className="fa fa-info-circle me-2 text-primary" style={{ fontSize: '0.9rem' }}></i>
                    Basic Information
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="row g-2">
                    <div className="col-12">
                      <label htmlFor="Name" className="form-label small mb-1">
                        Pond Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        id="Name"
                        value={formData.Name}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-12">
                      <label htmlFor="Location" className="form-label small mb-1">
                        Location <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control mb-2"
                        id="Location"
                        value={formData.Location}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="mb-2">
                        <TypeDropDownSelection selectedValue={selectedType} onChange={handleTypeChange} />
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <div className="mb-2">
                        <WaterSupplyDropDownSelection
                          allowMultiple={true}
                          selectedValue={selectedWaterSupply}
                          onChange={handleWaterSupplyChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Fish species + pond-level forage (per-visit forage remains on Stocking Data) */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-3 py-2 donezo-workflow-card__header">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title">
                    <i className="fa fa-fish me-2 text-info" style={{ fontSize: '0.9rem' }}></i>
                    Fish species
                  </h5>
                </div>
                <div className="card-body px-3 py-2">
                  <div className="mb-2">
                    <FishSpeciesDropDownSelection
                      allowMultiple={true}
                      selectedValue={selectedFishSpecies}
                      onChange={handleFishSpeciesChange}
                    />
                  </div>

                  <div className="mb-2">
                    <label className="form-label small mb-1">Forage types</label>
                    <ForageDropDownSelection
                      allowMultiple={true}
                      selectedValue={selectedForage}
                      onChange={handleForageChange}
                    />
                  </div>

                  <div className="mb-0">
                    <label htmlFor="ForageType" className="form-label small mb-1">
                      Additional forage type(s)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="ForageType"
                      value={formData.ForageType ?? ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Dates & Management Panel - LEFT SIDE */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3 donezo-workflow-card__header donezo-workflow-card__header--warm">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title donezo-workflow-card__title--lg">
                    <i className="fa fa-calendar-alt me-2 text-warning"></i>
                    Dates & Management
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12">
                      <AgeOfPondSelector
                        value={{ ageType: formData.AgeType, ageInYears: formData.AgeInYears }}
                        onChange={handleAgeOfPondChange}
                      />
                    </div>

                    {/* Estimated initial stocking date + supplemental stocking dates: hidden — values still load/save via API from formData */}

                    <div className="col-12">
                      <label htmlFor="BeginManagementDate" className="form-label small mb-1">
                        Begin Management Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        id="BeginManagementDate"
                        value={formData.BeginManagementDate}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Physical Characteristics Panel - RIGHT SIDE */}
            <div className="col-12 col-lg-6">
              <div className="card border-0 shadow-sm h-100 donezo-workflow-card">
                <div className="card-header border-0 bg-transparent px-4 py-3 donezo-workflow-card__header donezo-workflow-card__header--green">
                  <h5 className="mb-0 d-flex align-items-center donezo-workflow-card__title donezo-workflow-card__title--lg">
                    <i className="fa fa-ruler-combined me-2 text-success"></i>
                    Physical Characteristics
                  </h5>
                </div>
                <div className="card-body px-4 py-3">
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label htmlFor="Size" className="form-label small mb-1">
                        Size (acres) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="Size"
                        value={formData.Size}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="MaximumDepth" className="form-label small mb-1">
                        Max Depth (feet) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="MaximumDepth"
                        value={formData.MaximumDepth}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label htmlFor="AverageDepth" className="form-label small mb-1">
                        Average Depth (feet)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="AverageDepth"
                        value={formData.AverageDepth}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                      />
                    </div>

                    {formData.EstimatedVolume && (
                      <div className="col-12 col-md-6">
                        <label className="form-label small mb-1">
                          Estimated Volume (acre-feet)
                        </label>
                        <input
                          type="number"
                          className="form-control donezo-effort-readonly"
                          value={formData.EstimatedVolume.toFixed(2)}
                          readOnly
                          disabled
                        />
                      </div>
                    )}

                    <div className="col-12">
                      <label htmlFor="AquaticVegetationPresent" className="form-label small mb-1">
                        Aquatic Vegetation Present
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="AquaticVegetationPresent"
                        value={formData.AquaticVegetationPresent}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdatePond;